import {useEffect, useState} from 'react';
import {Button, Form, Modal} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import {Protocol, PublicAction, PublicFirewallRule} from '../../Types/routerForms';
import {IP_REGEX, newLocalId} from '../../Utils/random';

interface AddPublicRuleModalProps {
  show: boolean;
  onHide: () => void;
  onAdd: (rule: PublicFirewallRule) => void;
  destinations: string[];
}

const isOptionalPortValid = (p: string): boolean => {
  if (p === '') return true;
  if (/^\d+-\d+$/.test(p)) {
    const [a, b] = p.split('-').map(Number);
    return a >= 0 && b <= 65535 && a <= b;
  }
  const n = Number(p);
  return Number.isInteger(n) && n >= 0 && n <= 65535;
};

const AddPublicRuleModal = ({
  show,
  onHide,
  onAdd,
  destinations,
}: AddPublicRuleModalProps): JSX.Element => {
  const intl = useIntl();
  const [protocol, setProtocol] = useState<Protocol>('tcp');
  const [sourceIp, setSourceIp] = useState('');
  const [sourcePort, setSourcePort] = useState('');
  const [destinationIp, setDestinationIp] = useState(destinations[0] ?? '');
  const [destinationPort, setDestinationPort] = useState('');
  const [action, setAction] = useState<PublicAction>('accept');
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (show) {
      setProtocol('tcp');
      setSourceIp('');
      setSourcePort('');
      setDestinationIp(destinations[0] ?? '');
      setDestinationPort('');
      setAction('accept');
      setValidated(false);
    }
  }, [show, destinations]);

  const isSourceValid = sourceIp === '' || IP_REGEX.test(sourceIp.split('/')[0]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidated(true);
    if (!isSourceValid || !isOptionalPortValid(sourcePort) || !isOptionalPortValid(destinationPort))
      return;
    onAdd({
      id: newLocalId('pub'),
      protocol,
      sourceIp,
      sourcePort,
      destinationIp,
      destinationPort,
      action,
    });
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.ADD_RULE'})}
        </Modal.Title>
      </Modal.Header>
      <Form noValidate validated={validated} onSubmit={handleSubmit} id="addPublicRuleForm">
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PROTOCOL'})}
            </Form.Label>
            <Form.Select value={protocol} onChange={(e) => setProtocol(e.target.value as Protocol)}>
              <option value="tcp">TCP</option>
              <option value="udp">UDP</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.SOURCE_IP'})}
            </Form.Label>
            <Form.Control
              type="text"
              value={sourceIp}
              onChange={(e) => setSourceIp(e.target.value)}
              placeholder="(optionnel — toutes)"
              isInvalid={validated && !isSourceValid}
            />
            <Form.Control.Feedback type="invalid">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VALIDATION.IP'})}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.SOURCE_PORT'})}
            </Form.Label>
            <Form.Control
              type="text"
              value={sourcePort}
              onChange={(e) => setSourcePort(e.target.value)}
              placeholder="(optionnel — tous, ex: 80 ou 1024-2048)"
              isInvalid={validated && !isOptionalPortValid(sourcePort)}
            />
            <Form.Control.Feedback type="invalid">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VALIDATION.PORT'})}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="required">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.DEST_IP'})}
            </Form.Label>
            <Form.Select
              value={destinationIp}
              onChange={(e) => setDestinationIp(e.target.value)}
              required
            >
              {destinations.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.DEST_PORT'})}
            </Form.Label>
            <Form.Control
              type="text"
              value={destinationPort}
              onChange={(e) => setDestinationPort(e.target.value)}
              placeholder="(optionnel — tous)"
              isInvalid={validated && !isOptionalPortValid(destinationPort)}
            />
            <Form.Control.Feedback type="invalid">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VALIDATION.PORT'})}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label>
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.ACTION'})}
            </Form.Label>
            <Form.Select value={action} onChange={(e) => setAction(e.target.value as PublicAction)}>
              <option value="accept">
                {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.ACCEPT'})}
              </option>
              <option value="drop">
                {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.DROP'})}
              </option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            {intl.formatMessage({id: 'COMMON.CANCEL'})}
          </Button>
          <Button variant="primary" type="submit" form="addPublicRuleForm">
            {intl.formatMessage({id: 'COMMON.ADD'})}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddPublicRuleModal;
